import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Banner from '../../components/Banner';
import ConfirmDialog from '../../components/ConfirmDialog';
import LegalLinks from '../../components/LegalLinks';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getAnonymousSession } from '../../lib/anonymousSession';
import type { BillingPlan } from '../../lib/apiClient';

type BillingInterval = 'monthly' | 'yearly';

const DISCOUNT_CODE_STORAGE_KEY = 'taccounts_pending_discount_code';

function loadPendingDiscountCode(): string {
  if (new URLSearchParams(window.location.search).get('checkout') === 'success') {
    window.localStorage.removeItem(DISCOUNT_CODE_STORAGE_KEY);
    return '';
  }
  return window.localStorage.getItem(DISCOUNT_CODE_STORAGE_KEY) || '';
}

function formatPrice(amountCents: number, currency: string): string {
  const amount = amountCents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatNextChargeDate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(parsed);
}

export default function PlansPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    getBillingPlans,
    getBillingStatus,
    createBillingCheckoutSession,
    cancelBillingSubscription,
    validateDiscountCode,
  } = useApi();
  const [billingInterval, setBillingInterval] = React.useState<BillingInterval>('monthly');
  const [loading, setLoading] = React.useState(true);
  const [plans, setPlans] = React.useState<BillingPlan[]>([]);
  const [currentPlanCode, setCurrentPlanCode] = React.useState('free');
  const [nextChargeAt, setNextChargeAt] = React.useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = React.useState(false);
  const [checkoutAvailable, setCheckoutAvailable] = React.useState(false);
  const [checkoutBusyPlanCode, setCheckoutBusyPlanCode] = React.useState<string | null>(null);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [discountCode, setDiscountCode] = React.useState(loadPendingDiscountCode);
  const [validatedDiscount, setValidatedDiscount] = React.useState<{ code: string; trialDays: number } | null>(null);
  const [discountBusy, setDiscountBusy] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    const loadPlans = async () => {
      setLoading(true);

      const anonymousSession = getAnonymousSession();
      const request = {
        account_id: !user ? anonymousSession?.accountId : undefined,
        anonymous_user_id: user ? anonymousSession?.userId : undefined,
        anonymous_account_id: user ? anonymousSession?.accountId : undefined,
      };

      const [plansResult, statusResult] = await Promise.all([
        getBillingPlans(request, {
          shouldShowError: () => false,
        }),
        getBillingStatus(request, {
          shouldShowError: () => false,
        }),
      ]);

      if (!active) {
        return;
      }

      if (plansResult.ok) {
        setPlans(plansResult.data.plans);
        setCurrentPlanCode(plansResult.data.current_plan_code || 'free');
        setCheckoutAvailable(Boolean(plansResult.data.checkout_available));
      } else {
        setPlans([]);
        setCurrentPlanCode('free');
        setCheckoutAvailable(false);
      }

      if (statusResult.ok) {
        setNextChargeAt(statusResult.data.next_charge_at || null);
        setCancelAtPeriodEnd(Boolean(statusResult.data.cancel_at_period_end));

        if (!plansResult.ok) {
          setCurrentPlanCode(statusResult.data.plan_code || 'free');
          setCheckoutAvailable(Boolean(statusResult.data.checkout_available));
        }

        if ((statusResult.data.plan_code || '').toLowerCase() === 'pro') {
          if (statusResult.data.billing_interval === 'year') {
            setBillingInterval('yearly');
          } else if (statusResult.data.billing_interval === 'month') {
            setBillingInterval('monthly');
          }
        }
      } else {
        setNextChargeAt(null);
        setCancelAtPeriodEnd(false);
      }

      setLoading(false);
    };

    loadPlans();

    return () => {
      active = false;
    };
  }, [getBillingPlans, getBillingStatus, user]);

  const nextChargeLabel = formatNextChargeDate(nextChargeAt);

  const handleIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingInterval(event.target.value === 'yearly' ? 'yearly' : 'monthly');
  };

  const validateEnteredDiscount = async (): Promise<string | null> => {
    const normalized = discountCode.trim();
    if (!normalized) {
      setValidatedDiscount(null);
      window.localStorage.removeItem(DISCOUNT_CODE_STORAGE_KEY);
      return '';
    }
    if (validatedDiscount?.code.toLowerCase() === normalized.toLowerCase()) {
      return validatedDiscount.code;
    }

    setDiscountBusy(true);
    const result = await validateDiscountCode(normalized, { shouldShowError: () => true });
    setDiscountBusy(false);
    if (!result.ok) {
      setValidatedDiscount(null);
      return null;
    }

    const applied = { code: result.data.code, trialDays: result.data.trial_days };
    setDiscountCode(applied.code);
    setValidatedDiscount(applied);
    window.localStorage.setItem(DISCOUNT_CODE_STORAGE_KEY, applied.code);
    return applied.code;
  };

  const handlePlanAction = async (plan: BillingPlan) => {
    const normalizedCode = plan.plan_code.toLowerCase();
    const isCurrent = normalizedCode === currentPlanCode.toLowerCase();

    if (isCurrent) {
      return;
    }

    if (normalizedCode === 'pro') {
      const appliedDiscountCode = await validateEnteredDiscount();
      if (appliedDiscountCode === null) {
        return;
      }

      if (!user) {
        navigate('/auth?mode=signup');
        return;
      }

      if (!checkoutAvailable) {
        return;
      }

      const anonymousSession = getAnonymousSession();
      setCheckoutBusyPlanCode(plan.plan_code);

      const result = await createBillingCheckoutSession(
        {
          billing_interval: billingInterval === 'monthly' ? 'month' : 'year',
          anonymous_user_id: anonymousSession?.userId,
          anonymous_account_id: anonymousSession?.accountId,
          discount_code: appliedDiscountCode || undefined,
        },
        {
          shouldShowError: () => true,
        },
      );

      setCheckoutBusyPlanCode(null);

      if (result.ok && result.data.checkout_url) {
        window.location.assign(result.data.checkout_url);
      }
      return;
    }

    navigate('/documents');
  };

  const handleCancelPlan = async () => {
    if (!user || currentPlanCode.toLowerCase() !== 'pro' || cancelAtPeriodEnd || cancelBusy) {
      return;
    }

    setCancelConfirmOpen(true);
  };

  const handleConfirmCancelPlan = async () => {
    if (!user || currentPlanCode.toLowerCase() !== 'pro' || cancelAtPeriodEnd || cancelBusy) {
      return;
    }

    setCancelConfirmOpen(false);

    const anonymousSession = getAnonymousSession();
    setCancelBusy(true);

    const cancelResult = await cancelBillingSubscription(
      {
        anonymous_user_id: anonymousSession?.userId,
        anonymous_account_id: anonymousSession?.accountId,
      },
      {
        shouldShowError: () => true,
      },
    );

    if (cancelResult.ok) {
      setCancelAtPeriodEnd(Boolean(cancelResult.data.cancel_at_period_end));
      if (cancelResult.data.current_period_end) {
        setNextChargeAt(cancelResult.data.current_period_end);
      }

      const statusRefresh = await getBillingStatus(
        {
          anonymous_user_id: anonymousSession?.userId,
          anonymous_account_id: anonymousSession?.accountId,
        },
        {
          shouldShowError: () => false,
        },
      );

      if (statusRefresh.ok) {
        setCurrentPlanCode(statusRefresh.data.plan_code || 'free');
        setNextChargeAt(statusRefresh.data.next_charge_at || null);
        setCancelAtPeriodEnd(Boolean(statusRefresh.data.cancel_at_period_end));
      }
    }

    setCancelBusy(false);
  };

  const getActionLabel = (plan: BillingPlan) => {
    const normalizedCode = plan.plan_code.toLowerCase();
    const isCurrent = normalizedCode === currentPlanCode.toLowerCase();

    if (isCurrent) {
      return 'Current plan';
    }

    if (normalizedCode === 'pro') {
      if (!user) {
        return 'Sign up for Pro';
      }
      if (!checkoutAvailable) {
        return 'Upgrade (not configured)';
      }
      return 'Upgrade to Pro';
    }

    return 'Start free';
  };

  const isCurrentProAccount = currentPlanCode.toLowerCase() === 'pro';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default) }}>
      <Banner />
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1 }}>
          Plans
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            {plans.map((plan) => {
              const normalizedCode = plan.plan_code.toLowerCase();
              const isCurrent = normalizedCode === currentPlanCode.toLowerCase();
              const isProPlan = normalizedCode === 'pro';
              const isFreePlan = normalizedCode === 'free';
              const showFreePlanCancellationHint = isFreePlan && isCurrentProAccount;
              const monthlyPriceLabel = `${formatPrice(plan.pricing.monthly_amount_cents, plan.pricing.currency)}/month`;
              const yearlyPriceLabel = `${formatPrice(plan.pricing.yearly_amount_cents, plan.pricing.currency)}/year`;
              const proFreeTrialDays = Number(plan.free_trial_days ?? 0);
              const effectiveTrialDays = validatedDiscount?.trialDays ?? proFreeTrialDays;
              const intervalPrice = billingInterval === 'monthly'
                ? plan.pricing.monthly_amount_cents
                : plan.pricing.yearly_amount_cents;
              const priceLabel = intervalPrice > 0
                ? `${formatPrice(intervalPrice, plan.pricing.currency)}${billingInterval === 'monthly' ? '/month' : '/year'}`
                : 'Free';

              return (
                <Card
                  key={plan.plan_code}
                  sx={{
                    flex: 1,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    boxShadow: isCurrent ? 4 : 0,
                  }}
                >
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {plan.display_name}
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: 'text.secondary' }}>
                      {priceLabel}
                    </Typography>
                    {isProPlan && effectiveTrialDays > 0 ? (
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'success.main', fontWeight: 600 }}>
                        First {effectiveTrialDays} days free, cancel anytime
                      </Typography>
                    ) : null}
                    {isProPlan && !isCurrent ? (
                      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                        <FormControl>
                          <RadioGroup value={billingInterval} onChange={handleIntervalChange} aria-label="Pro billing interval">
                            <FormControlLabel value="monthly" control={<Radio size="small" />} label={`Monthly (${monthlyPriceLabel})`} />
                            <FormControlLabel value="yearly" control={<Radio size="small" />} label={`Yearly (${yearlyPriceLabel})`} />
                          </RadioGroup>
                        </FormControl>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            fullWidth
                            size="small"
                            label="Discount code"
                            value={discountCode}
                            disabled={discountBusy || checkoutBusyPlanCode === plan.plan_code}
                            onChange={(event) => {
                              const value = event.target.value;
                              setDiscountCode(value);
                              setValidatedDiscount(null);
                              if (value.trim()) {
                                window.localStorage.setItem(DISCOUNT_CODE_STORAGE_KEY, value.trim());
                              } else {
                                window.localStorage.removeItem(DISCOUNT_CODE_STORAGE_KEY);
                              }
                            }}
                            helperText={validatedDiscount ? `${validatedDiscount.trialDays}-day trial applied` : 'Optional'}
                            color={validatedDiscount ? 'success' : 'primary'}
                          />
                          <Button
                            variant="outlined"
                            disabled={!discountCode.trim() || discountBusy}
                            onClick={() => { void validateEnteredDiscount(); }}
                            sx={{ minWidth: 88, height: 40 }}
                          >
                            {discountBusy ? 'Checking' : 'Apply'}
                          </Button>
                        </Stack>
                      </Stack>
                    ) : null}
                    {isProPlan && isCurrent && nextChargeLabel ? (
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {cancelAtPeriodEnd ? `Your Pro plan will end on ${nextChargeLabel}` : `Next charge on ${nextChargeLabel}`}
                      </Typography>
                    ) : null}

                    <Stack spacing={0.75} sx={{ mt: 2.5, mb: 3.5 }}>
                      <Typography variant="body2">
                        {plan.limits.max_documents} documents per account
                      </Typography>
                      <Typography variant="body2">
                        {plan.limits.max_taccounts_per_document} T-accounts per document
                      </Typography>
                      <Typography variant="body2">
                        {plan.limits.max_transactions_per_document} transactions per document
                      </Typography>
                    </Stack>

                    {showFreePlanCancellationHint ? (
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', opacity: 0.72 }}
                      >
                        Cancelling your Pro plan will switch to the Free plan.
                      </Typography>
                    ) : null}

                    {isProPlan && isCurrent ? (
                      <Stack direction="row" spacing={1}>
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled
                        >
                          Current plan
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="inherit"
                          disabled={cancelBusy || cancelAtPeriodEnd}
                          onClick={() => {
                            void handleCancelPlan();
                          }}
                          sx={{ color: 'text.secondary', borderColor: 'divider', opacity: 0.85 }}
                        >
                          {cancelBusy ? 'Cancelling...' : cancelAtPeriodEnd ? 'Cancellation scheduled' : 'Cancel Plan'}
                        </Button>
                      </Stack>
                    ) : null}

                    {!showFreePlanCancellationHint && !(isProPlan && isCurrent) ? (
                      <Button
                        fullWidth
                        variant={isCurrent ? 'outlined' : 'contained'}
                        disabled={
                          isCurrent
                          || currentPlanCode.toLowerCase() === 'pro'
                          || checkoutBusyPlanCode === plan.plan_code
                          || (normalizedCode === 'pro' && Boolean(user) && !checkoutAvailable)
                        }
                        onClick={() => {
                          void handlePlanAction(plan);
                        }}
                      >
                        {checkoutBusyPlanCode === plan.plan_code ? 'Starting checkout...' : getActionLabel(plan)}
                      </Button>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
          For corporate or education licensing please contact{' '}
          <Link href="mailto:sales@t-accounts.com">sales@t-accounts.com</Link>
        </Typography>
      </Container>
      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Cancel Pro plan?"
        message="Cancel your Pro plan at the end of the current billing period? You will keep access until then."
        onConfirm={() => {
          void handleConfirmCancelPlan();
        }}
        onCancel={() => {
          if (!cancelBusy) {
            setCancelConfirmOpen(false);
          }
        }}
      />
      <Box component="footer" sx={{ pb: 3 }}>
        <LegalLinks centered />
      </Box>
    </Box>
  );
}
